import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { trackAmplitudeEvent } from '@/lib/amplitudeAnalytics';
import { ChunkLoadError, isChunkLoadError } from '@/lib/lazyWithRetry';
import {
  CHUNK_RELOAD_KEY,
  clearChunkReloadGuard,
  getChunkReloadAttemptCount,
  setChunkReloadGuard,
} from '@/lib/chunkReloadGuard';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Stale-bundle recovery state (the per-session reload guard) lives in
// `@/lib/chunkReloadGuard`, shared with `lazyWithRetry` so the guard is cleared
// event-based — when a dynamic import actually resolves — rather than on a timer
// (ENT-1981). Re-exported here for backwards-compatible imports/tests.
export { CHUNK_RELOAD_KEY };

const CHUNK_REFRESH_PARAM = 'sg_app_refresh';
const MAX_CHUNK_RELOAD_ATTEMPTS = 2;

const buildChunkRefreshUrl = (href: string, timestamp: number): string => {
  const url = new URL(href);
  url.searchParams.set(CHUNK_REFRESH_PARAM, String(timestamp));
  return url.toString();
};

const isCacheBustedChunkRefreshUrl = (href: string): boolean => {
  try {
    return new URL(href).searchParams.has(CHUNK_REFRESH_PARAM);
  } catch {
    return false;
  }
};

// Reload seam. Isolated behind an object property so tests can observe/replace
// the reload without redefining jsdom's unforgeable `window.location`.
export const chunkReloadController = {
  reload(): void {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  },
  forceRefresh(): void {
    clearChunkReloadGuard();
    if (typeof window !== 'undefined') {
      this.navigate(buildChunkRefreshUrl(window.location.href, Date.now()));
    }
  },
  navigate(url: string): void {
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
  },
  replace(url: string): void {
    if (typeof window !== 'undefined') {
      window.location.replace(url);
    }
  },
};

const isChunkError = (error: unknown): boolean =>
  error instanceof ChunkLoadError || isChunkLoadError(error);

/**
 * Recover from a stale chunk graph by reloading the document at most once per
 * browser session. The attempt is recorded as a per-session flag that is only
 * released when a dynamic import later resolves (event-based clear in
 * `lazyWithRetry`, ENT-1981). A slow boot, a long dynamic-import timeout, or a
 * persistent outage that re-throws much later can never re-arm the reload and
 * trap the user in a loop. After the single auto-reload the manual "Refresh
 * App" button remains the recovery path. Returns true if a reload was fired.
 */
const attemptChunkReload = (): boolean => {
  const attemptCount = getChunkReloadAttemptCount();
  if (attemptCount >= MAX_CHUNK_RELOAD_ATTEMPTS) {
    return false; // bounded recovery exhausted → show manual card
  }
  if (attemptCount > 0 && !isCacheBustedChunkRefreshUrl(window.location.href)) {
    return false; // already auto-reloaded this session → show manual card
  }
  if (!setChunkReloadGuard(attemptCount + 1)) {
    return false; // can't record the attempt → don't risk a reload loop
  }

  chunkReloadController.replace(buildChunkRefreshUrl(window.location.href, Date.now()));
  return true;
};

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    trackAmplitudeEvent('Error Encountered', {
      'error category': isChunkError(error) ? 'stale_chunk' : 'render',
      'error message': error.message,
      'error context': errorInfo.componentStack || 'react_error_boundary',
      'http status code': null,
      provider: 'react',
      'account id': null,
      'supplier id': null,
    });
    this.props.onError?.(error, errorInfo);

    // Auto-recover from a post-deploy stale bundle without making the user
    // hunt for a refresh (or, as happened in the field, open a private window).
    if (isChunkError(error)) {
      attemptChunkReload();
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (isChunkLoadError: boolean) => {
    if (isChunkLoadError) {
      chunkReloadController.forceRefresh();
      return;
    }

    chunkReloadController.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkLoadError = isChunkError(this.state.error);
      const title = isChunkLoadError ? 'App update detected' : 'Something went wrong';
      const description = isChunkLoadError
        ? 'Refresh to load the new version. You stay signed in.'
        : 'An error occurred while loading this page. Please try again.';

      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground font-mono overflow-auto max-h-32">
                  {this.state.error.message}
                </div>
              )}
              <div className="flex justify-center gap-2">
                {!isChunkLoadError && (
                  <Button onClick={this.handleRetry} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                )}
                <Button onClick={() => this.handleReload(isChunkLoadError)}>
                  {isChunkLoadError ? 'Refresh App' : 'Reload Page'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
